// #679 — pure helpers bridging GeoJSON storage and the location editor model.
//
// GeoJSON stores coordinates as [longitude, latitude]; the editor and the
// update API use an explicit {lat, lon} object. Keeping the translation in one
// tested place prevents a silent lon/lat swap.

export interface LatLon {
  lat: number;
  lon: number;
}

/** Convert a stored GeoJSON Point ([lon, lat]) to the editor's {lat, lon}. */
export function geoPointToLatLon(point: GeoJSON.Point | null): LatLon | null {
  if (!point || !Array.isArray(point.coordinates)) return null;
  const [lon, lat] = point.coordinates;
  return { lat, lon };
}

/** True when two coordinate pairs differ (null-aware). */
export function locationChanged(a: LatLon | null, b: LatLon | null): boolean {
  if (a === null || b === null) return a !== b;
  return a.lat !== b.lat || a.lon !== b.lon;
}

/** Shape the API expects for a location update ({lon, lat}). */
export function toLocationPayload(loc: LatLon): { lon: number; lat: number } {
  return { lon: loc.lon, lat: loc.lat };
}

/** Result shape of GET /api/var/validate-location. */
export interface ValidateLocationResult {
  error?: 'OUT_OF_BOUNDS';
  region?: { code: string; name: string };
}

export type RelocationStatus = 'ok' | 'out_of_bounds' | 'region_change';

/**
 * Decide whether an entered point warrants a warning, given the VAR's current
 * region code. "region_change" fires only when the point leaves that code —
 * mirroring the backend rule that a cross-region move must be a new VAR.
 */
export function evaluateRelocation(
  currentRegionCode: string | null,
  validation: ValidateLocationResult | null,
): RelocationStatus {
  if (!validation) return 'ok';
  if (validation.error === 'OUT_OF_BOUNDS') return 'out_of_bounds';
  if (validation.region && validation.region.code !== currentRegionCode) return 'region_change';
  return 'ok';
}
